/**
 * Jurisdiction-as-data for the SPA (CLA-859 Phase 2).
 *
 * The backend serves a `jurisdiction` block on `GET /api/v1/settings`
 * (see `Claro.Settings.JurisdictionDefaults.client_config/1`) so clients
 * render jurisdictional vocabulary — address labels, tax wording, currency,
 * practitioner registration terms, timezones, drug-schedule labels — from
 * DATA instead of `realm === 'us'` branches. A new region becomes a backend
 * seed, not a client release.
 *
 * Fallback story: the block is persisted to localStorage when a settings
 * response lands (see `useSettings` in api/hooks.ts). Pre-auth surfaces and
 * old backends that don't serve the block fall back to the built-in AU/US
 * datasets below, keyed by `pageRealm()` — which reproduce the backend seeds
 * exactly, so both sides degrade identically.
 *
 * Like realm.ts, this is a module helper, not React state: realm/jurisdiction
 * changes only happen across a full-page navigation (region switcher), so
 * non-reactive reads are the accepted pattern.
 */

import { pageRealm } from '@/lib/served-realm'

export interface JurisdictionStateOption {
  code: string
  name: string
}

export interface Jurisdiction {
  code: string
  locale: string
  address: {
    city_label: string
    postal_label: string
    state_label: string
    states: JurisdictionStateOption[]
    default_state: string | null
  }
  tax: {
    label: string
    line_label: string
    rate_pct: number
    inclusive: boolean
  }
  currency: {
    code: string
    symbol: string
  }
  patient_identifiers: {
    show_national: boolean
    search_hint: string
  }
  practitioner: {
    identifier_label: string
    registration_label: string
    registration_option_label: string
  }
  timezones: string[]
  default_timezone: string
  schedule_labels: Record<string, string>
  correspondence: {
    recipient_term: string
    recipient_descriptor: string
  }
  /** Gift card expiry rules (CLA-1949). Optional: a backend older than #1708 does not serve it. */
  gift_cards?: GiftCardRules
}

export interface GiftCardRules {
  /** A purchased card shorter than this is refused (AU ACL s 99B: 36; US CARD Act: 60). */
  min_expiry_months: number
  default_expiry_months: number
  /** A promotional voucher has no legal minimum. */
  promo_default_expiry_months: number
  tax_note: string
}

const STORAGE_KEY = 'claro:jurisdiction'

// --- Built-in datasets (pre-auth / old-backend fallback) --------------------
// These mirror `Claro.Settings.JurisdictionDefaults.@client_config` exactly.
// The AU seed reproduces the platform's historical hardcoded copy byte-for-byte.

const AU: Jurisdiction = {
  code: 'au',
  locale: 'en-AU',
  address: {
    city_label: 'Suburb',
    postal_label: 'Postcode',
    state_label: 'State',
    states: [
      { code: 'NSW', name: 'New South Wales' },
      { code: 'VIC', name: 'Victoria' },
      { code: 'QLD', name: 'Queensland' },
      { code: 'WA', name: 'Western Australia' },
      { code: 'SA', name: 'South Australia' },
      { code: 'TAS', name: 'Tasmania' },
      { code: 'NT', name: 'Northern Territory' },
      { code: 'ACT', name: 'Australian Capital Territory' },
    ],
    default_state: 'NSW',
  },
  tax: { label: 'GST', line_label: 'GST (10%)', rate_pct: 10, inclusive: true },
  currency: { code: 'AUD', symbol: 'A$' },
  patient_identifiers: {
    show_national: true,
    search_hint: 'Search by name, DOB, email, phone, Medicare...',
  },
  practitioner: {
    identifier_label: 'Provider number',
    registration_label: 'AHPRA registration number',
    registration_option_label: 'Registered practitioner (AHPRA)',
  },
  timezones: [
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Brisbane',
    'Australia/Perth',
    'Australia/Adelaide',
    'Australia/Hobart',
    'Australia/Darwin',
  ],
  default_timezone: 'Australia/Sydney',
  schedule_labels: {
    S3: 'Schedule 3 — Pharmacist only',
    S4: 'Schedule 4 — Prescription only',
    S8: 'Schedule 8 — Controlled drug',
  },
  correspondence: { recipient_term: 'GP', recipient_descriptor: "the patient's GP" },
  gift_cards: {
    min_expiry_months: 36,
    default_expiry_months: 36,
    promo_default_expiry_months: 12,
    tax_note: 'No GST when sold; GST applies to what the card pays for.',
  },
}

const US: Jurisdiction = {
  code: 'us',
  locale: 'en-US',
  address: {
    city_label: 'City',
    postal_label: 'ZIP code',
    state_label: 'State',
    states: [
      { code: 'AL', name: 'Alabama' },
      { code: 'AK', name: 'Alaska' },
      { code: 'AZ', name: 'Arizona' },
      { code: 'AR', name: 'Arkansas' },
      { code: 'CA', name: 'California' },
      { code: 'CO', name: 'Colorado' },
      { code: 'CT', name: 'Connecticut' },
      { code: 'DE', name: 'Delaware' },
      { code: 'DC', name: 'District of Columbia' },
      { code: 'FL', name: 'Florida' },
      { code: 'GA', name: 'Georgia' },
      { code: 'HI', name: 'Hawaii' },
      { code: 'ID', name: 'Idaho' },
      { code: 'IL', name: 'Illinois' },
      { code: 'IN', name: 'Indiana' },
      { code: 'IA', name: 'Iowa' },
      { code: 'KS', name: 'Kansas' },
      { code: 'KY', name: 'Kentucky' },
      { code: 'LA', name: 'Louisiana' },
      { code: 'ME', name: 'Maine' },
      { code: 'MD', name: 'Maryland' },
      { code: 'MA', name: 'Massachusetts' },
      { code: 'MI', name: 'Michigan' },
      { code: 'MN', name: 'Minnesota' },
      { code: 'MS', name: 'Mississippi' },
      { code: 'MO', name: 'Missouri' },
      { code: 'MT', name: 'Montana' },
      { code: 'NE', name: 'Nebraska' },
      { code: 'NV', name: 'Nevada' },
      { code: 'NH', name: 'New Hampshire' },
      { code: 'NJ', name: 'New Jersey' },
      { code: 'NM', name: 'New Mexico' },
      { code: 'NY', name: 'New York' },
      { code: 'NC', name: 'North Carolina' },
      { code: 'ND', name: 'North Dakota' },
      { code: 'OH', name: 'Ohio' },
      { code: 'OK', name: 'Oklahoma' },
      { code: 'OR', name: 'Oregon' },
      { code: 'PA', name: 'Pennsylvania' },
      { code: 'RI', name: 'Rhode Island' },
      { code: 'SC', name: 'South Carolina' },
      { code: 'SD', name: 'South Dakota' },
      { code: 'TN', name: 'Tennessee' },
      { code: 'TX', name: 'Texas' },
      { code: 'UT', name: 'Utah' },
      { code: 'VT', name: 'Vermont' },
      { code: 'VA', name: 'Virginia' },
      { code: 'WA', name: 'Washington' },
      { code: 'WV', name: 'West Virginia' },
      { code: 'WI', name: 'Wisconsin' },
      { code: 'WY', name: 'Wyoming' },
    ],
    default_state: null,
  },
  // rate_pct 0 until Stripe Tax computes real US sales tax — a US buyer
  // must never be charged Australian GST.
  tax: { label: 'Tax', line_label: 'Tax', rate_pct: 0, inclusive: false },
  currency: { code: 'USD', symbol: '$' },
  patient_identifiers: {
    show_national: false,
    search_hint: 'Search by name, DOB, email, phone...',
  },
  practitioner: {
    identifier_label: 'NPI',
    registration_label: 'Medical license number',
    registration_option_label: 'Licensed practitioner',
  },
  timezones: [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
  ],
  default_timezone: 'America/New_York',
  schedule_labels: {
    RX: 'Prescription only',
    OTC: 'Over the counter',
    C2: 'Controlled — Schedule II',
    C3: 'Controlled — Schedule III',
    C4: 'Controlled — Schedule IV',
    C5: 'Controlled — Schedule V',
  },
  correspondence: {
    recipient_term: 'referring physician',
    recipient_descriptor: "the patient's referring physician or PCP",
  },
  gift_cards: {
    min_expiry_months: 60,
    default_expiry_months: 60,
    promo_default_expiry_months: 12,
    tax_note: 'No sales tax when sold; tax applies to what the card pays for.',
  },
}

const BUILTINS: Record<string, Jurisdiction> = { au: AU, us: US }

// --- Server-block persistence -----------------------------------------------

let cached: Jurisdiction | null = null

/**
 * Persist the jurisdiction block from a settings response. Called wherever
 * the settings payload lands (useSettings). Ignores absent/malformed blocks
 * (old backend) so the realm-keyed builtin keeps covering.
 */
export function setJurisdiction(block: unknown): void {
  if (!block || typeof block !== 'object') return
  const j = block as Jurisdiction
  // Minimal shape check — enough to know it's a real jurisdiction block and
  // won't crash the accessors.
  if (typeof j.code !== 'string' || !j.address || !j.tax || !j.currency) return
  cached = j
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(j))
  } catch {
    /* storage unavailable — module cache still covers this session */
  }
}

/**
 * The active jurisdiction: server-provided block if we have one (module
 * cache, then localStorage), else the realm-keyed builtin.
 */
// After a region switch (staff), the OTHER known realm's persisted block must
// not leak into pre-auth surfaces — but an unknown code (a future region the
// realm helper can't represent) is trusted: it can only have come from the
// backend this deployment talks to. The builtin covers the gap until settings
// re-land.
function staleForRealm(j: Jurisdiction, realm: string): boolean {
  return j.code !== realm && j.code in BUILTINS
}

export function jurisdiction(): Jurisdiction {
  // The page's realm: a signed-out US patient (booking, portal) gets the US
  // builtin — ZIP code, sales tax — not AU's (CLA-1829).
  const realm = pageRealm()
  if (cached && !staleForRealm(cached, realm)) return cached
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const j = JSON.parse(raw) as Jurisdiction
      if (j && typeof j.code === 'string' && j.address && j.tax && j.currency && !staleForRealm(j, realm)) {
        cached = j
        return j
      }
    }
  } catch {
    /* corrupt/unavailable storage — fall through to builtin */
  }
  return BUILTINS[realm] ?? AU
}

/**
 * The jurisdiction dataset for an EXPLICIT code, independent of the current
 * deployment's realm. Almost every surface wants `jurisdiction()` (keyed on the
 * realm the user is authenticated into). The exception is the signup page, where
 * the user PICKS a jurisdiction that may differ from the realm serving the page
 * (a US clinic starting on the AU deployment before the wrong-realm redirect) —
 * there the form's vocabulary must follow the picked code, not the realm.
 *
 * Prefers a server block whose code matches (freshest copy for the current
 * realm), else the builtin for that code, else the realm's active jurisdiction.
 */
export function jurisdictionFor(code: string): Jurisdiction {
  if (cached && cached.code === code) return cached
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const j = JSON.parse(raw) as Jurisdiction
      if (j && j.code === code && j.address && j.tax && j.currency) return j
    }
  } catch {
    /* corrupt/unavailable storage — fall through to builtin */
  }
  return BUILTINS[code] ?? jurisdiction()
}

// --- Label accessors ---------------------------------------------------------

/** BCP 47 locale for date/number formatting (AU "en-AU" / US "en-US"). */
export function jLocale(): string {
  return jurisdiction().locale
}

/** Address line-2 locality label: AU "Suburb" / US "City". */
export function suburbLabel(): string {
  return jurisdiction().address.city_label
}

/** Postal-code label: AU "Postcode" / US "ZIP code". */
export function postcodeLabel(): string {
  return jurisdiction().address.postal_label
}

/** State/territory field label (both realms: "State"). */
export function stateLabel(): string {
  return jurisdiction().address.state_label
}

/** State/territory options for address selects. */
export function stateOptions(): JurisdictionStateOption[] {
  return jurisdiction().address.states
}

/** Pre-selected state for new addresses (AU "NSW"; US null = force a choice). */
export function defaultState(): string | null {
  return jurisdiction().address.default_state
}

/** Consumption-tax label: AU "GST" / US "Tax". */
export function taxLabel(): string {
  return jurisdiction().tax.label
}

/** Cart/checkout tax row label: AU "GST (10%)" / US "Tax". */
export function taxLineLabel(): string {
  return jurisdiction().tax.line_label
}

/** Tax rate as a percentage (AU 10; US 0 until Stripe Tax computes it). */
export function taxRatePct(): number {
  return jurisdiction().tax.rate_pct
}

/** Whether displayed prices include tax (AU true / US false). */
export function taxInclusive(): boolean {
  return jurisdiction().tax.inclusive
}

/** ISO 4217 code for amounts the server sent without one: AU "AUD" / US "USD". */
export function currencyCode(): string {
  return jurisdiction().currency.code
}

/** Currency symbol for price display: AU "A$" / US "$". */
export function currencySymbol(): string {
  return jurisdiction().currency.symbol
}

/**
 * The primary-care correspondent, as used mid-sentence: AU "GP",
 * US "referring physician".
 */
export function gpTerm(): string {
  return jurisdiction().correspondence.recipient_term
}

/**
 * The correspondent as a full mid-sentence noun phrase: AU "the patient's GP",
 * US "the patient's referring physician or PCP".
 */
export function gpDescriptor(): string {
  return jurisdiction().correspondence.recipient_descriptor
}

/** Timezone options for scheduling selects. */
export function tzOptions(): string[] {
  return jurisdiction().timezones
}

/** Default IANA timezone (AU "Australia/Sydney" / US "America/New_York"). */
export function defaultTimezone(): string {
  return jurisdiction().default_timezone
}

/**
 * Human label for a drug-schedule code (AU S3/S4/S8, US RX/OTC/C2–C5).
 * Unknown codes fall back to the raw code.
 */
export function scheduleLabel(code: string): string {
  return jurisdiction().schedule_labels[code] ?? code
}

/** Whether national patient identifiers (Medicare/IHI/DVA) render at all. */
export function showNationalIdentifiers(): boolean {
  return jurisdiction().patient_identifiers.show_national
}

/** Placeholder for the patient search box. */
export function patientSearchHint(): string {
  return jurisdiction().patient_identifiers.search_hint
}

/** Practitioner identifier/registration labels (AHPRA vs NPI/license). */
export function practitionerLabels(): Jurisdiction['practitioner'] {
  return jurisdiction().practitioner
}

/**
 * Practitioner labels for an explicit jurisdiction code — for the signup form,
 * whose picked jurisdiction can differ from the deployment realm. See
 * `jurisdictionFor`.
 */
export function practitionerLabelsFor(code: string): Jurisdiction['practitioner'] {
  return jurisdictionFor(code).practitioner
}

/**
 * Gift card expiry rules for the realm (CLA-1949): the server block when it
 * carries them, else the built-in for the realm (which mirrors the backend
 * seed), so an older cached block without `gift_cards` still gets the right
 * legal minimum.
 */
export function giftCardRules(): GiftCardRules {
  const j = jurisdiction()
  return j.gift_cards ?? BUILTINS[j.code]?.gift_cards ?? BUILTINS[pageRealm()]?.gift_cards ?? AU.gift_cards!
}
